import { Link, router } from "@inertiajs/react"
import { CheckCircle2, Video } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { SubmissionReviewCard } from "@/components/submissions/submission-review-card"
import { Button } from "@/components/ui/button"
import { missionsPath } from "@/routes"
import type { SubmissionsToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-707 — Submissions-to-review: the SECOND consumer of the reusable
 * {@link HomeTriageSection} shell (OKS-706 candidates being the first).
 * Wires the submission card grid + the cold-start / empty body slots and
 * owns the Retry round-trip. Mirrors `CandidatesToReviewSection` so the
 * two home sections read as siblings.
 *
 * Mounted inside an Inertia `<Deferred data="submissions_to_review">` on
 * `Brand/Home`, so `section` is always resolved here; the deferred-pending
 * first paint renders the shell's `loading` skeleton via the `<Deferred>`
 * fallback. Retry re-fetches ONLY this prop (`router.reload`) and flips a
 * local `loading` state so the skeleton shows immediately on click.
 */
export interface SubmissionsToReviewSectionProps {
  section: SectionData
  /**
   * OKS-923 — these are the last-known rows and a round-trip is settling.
   * Set by the `<Deferred>` fallback, which is the only place that knows the
   * prop is currently absent.
   */
  isRefreshing?: boolean
  /**
   * OKS-923 — the refresh window expired without the prop arriving. Forces the
   * shell's EXISTING error state, whose Retry is already wired below: an
   * unresolved refresh must resolve into either truth or an error affordance,
   * never into silence (PRD R6 / UX Spec §3).
   */
  refreshFailed?: boolean
}

export function SubmissionsToReviewSection({
  section,
  isRefreshing = false,
  refreshFailed = false,
}: SubmissionsToReviewSectionProps) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["submissions_to_review"],
      onFinish: () => setIsRetrying(false),
    })
  }

  const state = isRetrying ? "loading" : refreshFailed ? "error" : section.state

  return (
    <HomeTriageSection
      title={t("submissions.home_review.title")}
      state={state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      isRefreshing={isRefreshing}
      testId="submissions-to-review"
      renderColdStart={() => (
        <EmptyState
          icon={Video}
          title={t("submissions.home_review.cold_start.title")}
          titleAs="h2"
          description={t("submissions.home_review.cold_start.description")}
          data-testid="submissions-to-review-cold-start"
          actions={
            <Button asChild variant="outline">
              <Link href={missionsPath()}>
                {t("submissions.home_review.cold_start.cta")}
              </Link>
            </Button>
          }
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={CheckCircle2}
          title={t("submissions.home_review.empty.title")}
          titleAs="h2"
          description={t("submissions.home_review.empty.description")}
          data-testid="submissions-to-review-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <SubmissionReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
